import { DATA_BASE_URL } from './liveOddsVnextContract.js'

const IDENTITY_FIELDS = Object.freeze(['book', 'league', 'event', 'et_date', 'bet_type', 'period', 'selection', 'line'])
const LEGACY_IDENTITY_FIELDS = Object.freeze(['book', 'league', 'bet_type', 'selection', 'line'])

function token(value) {
  return value == null || value === '' ? '' : String(value).trim().toLowerCase()
}

function safeCategorySlug(slug) {
  if (typeof slug !== 'string' || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    throw new TypeError('category slug is unsafe')
  }
  return slug
}

async function fetchJson(url, fetchImpl) {
  const response = await fetchImpl(url, { cache: 'no-store' })
  if (!response?.ok || typeof response.json !== 'function') {
    throw new Error(`evidence request failed (HTTP ${response?.status ?? 'unavailable'})`)
  }
  return response.json()
}

export function auditProvenanceRecord(record, row) {
  if (record?.resolution === 'unresolved' || (Object.hasOwn(record || {}, 'source') && !record.source)) {
    return { status: 'unverifiable', matches: false, comparedFields: [], mismatchFields: [] }
  }
  const wrappedIdentity = record?.identity
  const identity = wrappedIdentity || record?.leaf
  if (!identity || typeof identity !== 'object' || !row || typeof row !== 'object') {
    return { status: 'unverifiable', matches: false, comparedFields: [], mismatchFields: [] }
  }
  const comparedFields = IDENTITY_FIELDS.filter((field) => Object.hasOwn(identity, field))
  const mismatchFields = comparedFields.filter((field) => {
    const source = field === 'period' ? identity[field] || 'Game' : identity[field]
    const selected = field === 'period' ? row[field] || 'Game' : row[field]
    return token(source) !== token(selected)
  })
  const streamIdentityMatches = record.stream_id && row.stream_id && record.stream_id === row.stream_id
  if (!streamIdentityMatches && record.offer_id && row.offer_id && record.offer_id !== row.offer_id) {
    mismatchFields.push('offer_id')
  }
  if (record.stream_id && row.stream_id && !streamIdentityMatches) mismatchFields.push('stream_id')
  const requiredFields = wrappedIdentity ? IDENTITY_FIELDS : LEGACY_IDENTITY_FIELDS
  const complete = requiredFields.every((field) => comparedFields.includes(field))
  const status = mismatchFields.length > 0 ? 'mismatch' : complete ? 'verified' : 'unverifiable'
  return { status, matches: status === 'verified', comparedFields, mismatchFields }
}

export async function fetchLiveOddsHistory({ fetchImpl = fetch } = {}) {
  const value = await fetchJson(`${DATA_BASE_URL}/crossbook_history.json`, fetchImpl)
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError('live odds history must be an object')
  }
  return value
}

export async function fetchLiveOddsProvenance(slug, pid, { fetchImpl = fetch, expectedRow = null } = {}) {
  const safeSlug = safeCategorySlug(slug)
  if (!Number.isSafeInteger(pid) || pid < 0) throw new TypeError('pid must be a non-negative integer')
  const records = await fetchJson(`${DATA_BASE_URL}/prov_${safeSlug}.json`, fetchImpl)
  if (!Array.isArray(records)) throw new TypeError('provenance artifact must be an array')
  const directRecord = records[pid] ?? null
  const directAudit = auditProvenanceRecord(directRecord, expectedRow)
  if (!expectedRow || directAudit.matches) {
    return { slug: safeSlug, requestedPid: pid, resolvedPid: pid, record: directRecord, audit: directAudit, resolution: 'exact_index' }
  }
  const matches = []
  records.forEach((record, index) => {
    if (auditProvenanceRecord(record, expectedRow).matches) matches.push({ index, record })
  })
  if (matches.length === 1) {
    return { slug: safeSlug, requestedPid: pid, resolvedPid: matches[0].index, record: matches[0].record,
      audit: auditProvenanceRecord(matches[0].record, expectedRow), resolution: 'identity_recovered' }
  }
  return { slug: safeSlug, requestedPid: pid, resolvedPid: null, record: directRecord, audit: directAudit,
    resolution: matches.length > 1 ? 'identity_ambiguous' : directAudit.status }
}
